import { getAddress, isAddress, zeroAddress, type Address } from 'viem';

// Kept in its own module so the app config can parse the router address without pulling the router ABI
// into the core bundle while the feature is off.

/** Valid, non-zero address in checksum form, or null. Use it to parse the configured router address. */
export const parseRouterAddress = (value: string | undefined | null): Address | null => {
  if (!value || !isAddress(value, { strict: false })) return null;
  const address = getAddress(value);
  return address === zeroAddress ? null : address;
};

export type RouterConfig = { status: 'off' } | { status: 'on'; router: Address; factory: Address; ctf: Address };

const isBlank = (value: string | undefined | null) => !value || value.trim() === '';

const requireAddress = (field: string, value: string | undefined | null): Address => {
  const address = parseRouterAddress(value?.trim());
  if (!address) throw new Error(`APMM router config: ${field} is not a valid non-zero address`);
  return address;
};

/**
 * Resolves the router feature switch from the three configured addresses.
 * Off only when router and factory are both empty. Any other incomplete or invalid combination throws,
 * so a half-set or mistyped config can not turn the feature on without a check, or off by accident.
 * When on, the ctf address must be valid too: verifyRouter() compares router.ctf() with it.
 */
export const resolveRouterConfig = (p: {
  router?: string | null;
  factory?: string | null;
  ctf?: string | null;
}): RouterConfig => {
  const routerBlank = isBlank(p.router);
  const factoryBlank = isBlank(p.factory);
  if (routerBlank && factoryBlank) return { status: 'off' };
  if (routerBlank) throw new Error('APMM router config: factory is set but router is empty');
  if (factoryBlank) throw new Error('APMM router config: router is set but factory is empty');
  return {
    status: 'on',
    router: requireAddress('router', p.router),
    factory: requireAddress('factory', p.factory),
    ctf: requireAddress('ctf', p.ctf),
  };
};

/** Build-time check: runs resolveRouterConfig on the env of the vite mode. Throws on a bad config. */
export const assertRouterEnv = (env: Record<string, string | undefined>): RouterConfig =>
  resolveRouterConfig({
    router: env.VITE_DPIN_ROUTER_ADDRESS,
    factory: env.VITE_DPIN_FACTORY_ADDRESS,
    ctf: env.VITE_CTF_ADDRESS,
  });
